import { useMemo, useCallback } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Button } from 'antd'
import { Icon, Link, T, Table, Tooltip } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import {
  AITrainingMaterialFragment,
  AIVectorStoreProcessingStatus,
  AIVectorStoreType,
} from 'types/graphql'

import { RightAlignedCellContainer } from 'src/components/shared/StyledComponents'
import { useGetAllAITrainingMaterialsQuery } from 'src/fetch/aiTrainingMaterials'
import { useDisclosure } from 'src/hooks/use-disclosure'
import { MimeTypeDisplayNameMap, MimeType } from 'src/utils/file'

import AITrainingMaterialsTableButtons from '../../pages/AIAssistantTrainPage/AITrainingMaterialsTableButtons'

import UploadAITrainingMaterialsModal from './UploadAITrainingMaterialsModal'

const getStatusIcon = (
  status: AIVectorStoreProcessingStatus | null | undefined
): { icon: React.ReactNode; tooltip: string } => {
  switch (status) {
    case 'Pending':
    case 'Processing':
      return {
        icon: <Icon.FaSpinner className="animate-spin text-blue-600" />,
        tooltip: 'Processing',
      }
    case 'Completed':
      return {
        icon: <Icon.FaCircleCheckSolid className="text-green-700" />,
        tooltip: 'Uploaded',
      }
    case 'Failed':
      return {
        icon: <Icon.FaCircleExclamationSolid className="text-red-600" />,
        tooltip: 'Failed',
      }
    default:
      return {
        icon: <Icon.FaSpinner className="animate-spin text-blue-600" />,
        tooltip: 'Processing',
      }
  }
}

const AITrainingMaterialsTable = ({
  vectorStoreType,
  title,
}: {
  vectorStoreType: AIVectorStoreType
  title: string
}) => {
  const tableProps = useTableState<AITrainingMaterialFragment>()
  const { materials, isFetching, refetch } = useGetAllAITrainingMaterialsQuery(vectorStoreType)
  const anyMaterialHasCitations = useMemo(
    () => materials.some((m) => !!m.citationCount),
    [materials]
  )

  const modalState = useDisclosure()

  const getRowId = useCallback((d: AITrainingMaterialFragment) => `${d.id}`, [])

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<AITrainingMaterialFragment>()
    return [
      columnHelper.accessor((d) => d.file?.name || '', {
        id: 'name',
        header: 'File Name',
        cell: ({ row: { original: d } }) => (
          <T.Body>
            <Link externalHref={d.file?.url?.signedUrl || ''}>{d.file?.name}</Link>
          </T.Body>
        ),
      }),
      columnHelper.accessor((d) => d.file?.type || '', {
        id: 'type',
        header: 'Type',
        cell: (info) => MimeTypeDisplayNameMap[info.getValue() as MimeType],
      }),
      columnHelper.accessor((d) => d.processingStatus, {
        id: 'status',
        header: 'Status',
        cell: ({ row: { original: d } }) => {
          const { icon, tooltip } = getStatusIcon(d.processingStatus)
          const errorTooltip = d.processingError ? `${tooltip}: ${d.processingError}` : tooltip
          return (
            <Tooltip content={errorTooltip}>
              <span>{icon}</span>
            </Tooltip>
          )
        },
      }),
      ...(anyMaterialHasCitations
        ? [
            columnHelper.accessor((d) => (d.citationCount ? `${d.citationCount}` : ''), {
              id: 'citations',
              header: 'Citations',
              cell: (info) => info.getValue(),
            }),
          ]
        : []),
      columnHelper.display({
        id: 'actions',
        header: '',
        meta: { textAlign: 'right' },
        cell: ({ row: { original: material } }) => (
          <RightAlignedCellContainer>
            <AITrainingMaterialsTableButtons material={material} refetch={refetch} />
          </RightAlignedCellContainer>
        ),
      }),
    ]
  }, [refetch, anyMaterialHasCitations])

  const actions = useMemo(() => {
    return (
      <Button icon={<Icon.FaArrowUpFromBracket />} type="primary" onClick={modalState.open}>
        Upload files
      </Button>
    )
  }, [modalState])

  return (
    <>
      <Table
        actions={actions}
        title={title}
        showCount
        totalCount={materials.length}
        columns={columns}
        data={materials}
        getRowId={getRowId}
        isLoading={isFetching}
        isSortedOnClient
        emptyStateMessage="No training materials uploaded yet"
        {...tableProps}
      />
      {modalState.isOpen && (
        <UploadAITrainingMaterialsModal vectorStoreType={vectorStoreType} modalState={modalState} />
      )}
    </>
  )
}

export default AITrainingMaterialsTable
